/**
 * Shared design-details status helpers (spec BM-9436 §5 / Appendix §15).
 *
 * `status` is the stored string enum:
 *   'approved' | 'rejected' | 'approved_with_comments' | (absent) -> pending
 * The three states are first-class values (no derivation from comment presence).
 * `hasStatusComment` is retained only to validate the mandatory comment for the
 * Approved-with-Comments status before submit.
 */

/** The three stored design approval states (lowercase); anything else is pending (null). */
const DESIGN_STATUS_KEYS = ['approved', 'rejected', 'approved_with_comments'];

/**
 * @description True when a status comment holds real text. Empty CKEditor markup
 * such as `<p></p>`, `<br>` or `&nbsp;` is treated as no comment (spec §11).
 * @param {string} [statusComment] Raw comment string (may contain HTML).
 * @returns {boolean} Whether a meaningful comment is present.
 */
export const hasStatusComment = (statusComment) => {
  if (!statusComment) {
    return false;
  }
  const stripped = String(statusComment)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return stripped.length > 0;
};

/**
 * @description Resolve the display key for a design canvas from its stored status
 * enum. Keys map to `common` i18n `TAKING_DATES.*` labels. Any value that is not one
 * of the three enum members (including unset) is treated as pending (null).
 * @param {('approved'|'rejected'|'approved_with_comments')} [status] Stored status enum.
 * @returns {('rejected'|'approved'|'approved_with_comments'|null)} Display key, or null when not actioned.
 */
export const deriveDesignStatusKey = (status) => (DESIGN_STATUS_KEYS.includes(status) ? status : null);

/**
 * @description Map a derived status key to its status colour class used across
 * design-details screens (`green` / `red` defined in DesignDetail.css). Both
 * approved states are green; only rejection is red.
 * @param {('rejected'|'approved'|'approved_with_comments'|null)} statusKey Derived key.
 * @returns {string} CSS colour class, or empty string when not actioned.
 */
export const statusColorClass = (statusKey) => {
  switch (statusKey) {
    case 'approved':
    case 'approved_with_comments':
      return 'green';
    case 'rejected':
      return 'red';
    default:
      return '';
  }
};
